import type { LabelGroup, LabelGroups } from '../util';

import { PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react';
import { Fragment } from 'react';
import { css, html } from 'react-strict-dom';
import { Api, useApiCallable } from 'src/api';
import {
  Dropdown,
  DropdownMenuItem,
  IconButton,
  Stack,
  Text,
} from 'src/components';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { relationsAsIri, values } from 'src/util';

import { findLabelGroupByName, isInGroup } from '../util';
import TicketBadge from './TicketBadge';

type Props = {
  ticket: Api.Ticket.V_Detail;
  labelGroups: LabelGroups;
  invalidate: () => void;
};

const byName = (a: Api.TicketLabel.BaseView, b: Api.TicketLabel.BaseView) =>
  a.name.localeCompare(b.name);

/**
 * The label colour is the group's rather than the label's for the two groups
 * that read as a state; everything else keeps whatever colour it was given.
 */
function badgeColor(
  group: LabelGroup,
  label: Api.TicketLabel.BaseView
): string | undefined {
  if (group.name === 'Blockers') return Color.accent;
  if (group.name === 'Resolution') return Color.blue;
  return label.color ? `#${label.color}` : undefined;
}

/**
 * Every label group, each edited in place from its own dropdown. This is the
 * only way to change a ticket's labels — there is no separate drawer.
 */
export default function LabelsCard({ ticket, labelGroups, invalidate }: Props) {
  const callApi = useApiCallable();
  const groups = values(labelGroups);
  // Priority holds one label at a time; every other group is a free toggle.
  const priorityGroup = findLabelGroupByName(labelGroups, 'Priority');

  if (groups.length === 0) return null;

  async function setLabels(next: ReadonlyArray<Api.TicketLabel.BaseView>) {
    await callApi(`/tickets/${ticket.id}`, {
      method: 'PUT',
      jsonBody: { labels: relationsAsIri(next) },
    });
    invalidate();
  }

  function toggleLabel(group: LabelGroup, label: Api.TicketLabel.BaseView) {
    const isSelected = ticket.labels.some((l) => l.id === label.id);
    const isSingle = group.id === priorityGroup?.id;
    const withoutGroup = ticket.labels.filter((l) => !isInGroup(group)(l));

    if (isSingle) {
      return setLabels(isSelected ? withoutGroup : [...withoutGroup, label]);
    }
    return setLabels(
      isSelected
        ? ticket.labels.filter((l) => l.id !== label.id)
        : [...ticket.labels, label]
    );
  }

  return (
    <Stack gap={1} style={styles.card}>
      {groups.map((group, i) => {
        const active = [...ticket.labels.filter(isInGroup(group))].sort(byName);

        return (
          <Fragment key={group.id}>
            {i > 0 && <html.hr style={styles.divider} />}
            <Stack gap={0.25}>
              <Stack style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
                <Text typeface="body.regular" color="neutral80">
                  {group.name}
                </Text>
                <Dropdown
                  trigger={
                    <IconButton aria-label={`Edit ${group.name}`}>
                      {active.length > 0 ? (
                        <PencilSimpleIcon size={20} color={Color.neutral60} />
                      ) : (
                        <PlusIcon size={20} color={Color.neutral60} />
                      )}
                    </IconButton>
                  }
                  anchorSide="right"
                >
                  <Dropdown.SectionHeader>{group.name}</Dropdown.SectionHeader>
                  {[...group.labels].sort(byName).map((label) => (
                    <DropdownMenuItem
                      key={label.id}
                      showRadio={false}
                      selected={ticket.labels.some((l) => l.id === label.id)}
                      onClick={() => toggleLabel(group, label)}
                    >
                      {label.name}
                    </DropdownMenuItem>
                  ))}
                </Dropdown>
              </Stack>
              {active.length > 0 && (
                // Type reads as a field value rather than a state, so it stays
                // plain text, one per line. Everything else is a chip, and
                // chips wrap.
                <html.div
                  style={
                    group.name === 'Type' ? styles.textValues : styles.chips
                  }
                >
                  {active.map((label) =>
                    group.name === 'Type' ? (
                      <Text
                        key={label.id}
                        typeface="body.regular"
                        color="neutral100"
                      >
                        {label.name}
                      </Text>
                    ) : (
                      <TicketBadge
                        key={label.id}
                        label={label.name}
                        color={badgeColor(group, label)}
                        bordered
                      />
                    )
                  )}
                </html.div>
              )}
            </Stack>
          </Fragment>
        );
      })}
    </Stack>
  );
}

const styles = css.create({
  card: {
    backgroundColor: color.neutral0,
    borderRadius: 12,
    padding: 16,
    boxShadow: '0px 16px 16px rgba(0, 0, 0, 0.04)',
  },
  divider: {
    width: '100%',
    height: 0,
    borderWidth: 0,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: color.neutral20,
    margin: 0,
  },
  chips: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
  },
  textValues: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
});
